import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, IconButton, Tooltip, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Snackbar, Alert,
} from '@mui/material';
import { FaObjectGroup, FaObjectUngroup } from 'react-icons/fa';
import { toBrazilianDate } from '../../../../helpers/utils/utils';
import { hasUserPermissions } from '../../../../helpers/utils/permissoes';
import { unificarProcessosService, juntarProcessosService } from '../../../../services/core/processos/processoAntecedenteService';
import { tiposConsultasAntecedentesTypes } from '../../../../types/tiposConsultasAntecedentesTypes';
import { getMensagemConfirmacao } from './antecedentesController';
import * as C from '../../../theme/colors.constants';

const PERMISSAO_UNIFICAR = 1204;
const PERMISSAO_JUNTAR = 1205;
const MENSAGEM_ERRO_PADRAO = 'Não foi possível concluir a operação.';

/**
 * Port de `ResumoAntecedenteProcesso.jsx`/`AntecedentesProcessos.jsx` reais
 * (AW2-1033 Etapas 3/4 + ajuste pós-Etapa 5) — 2 ícones Unificar/Juntar
 * (`FaObjectGroup`/`FaObjectUngroup`, mesmos ícones da tela real, só
 * retemados) + card de resumo do processo principal INLINE (voltou a ser
 * `Box`, não `Dialog` — decisão do usuário revertendo um ajuste anterior) +
 * dialog de confirmação com a mutação real (`unificarProcessosService`/
 * `juntarProcessosService`, sem modificação).
 *
 * `tipoConsultaAntecedentes`/`onAlternarTipo` (props, não mais state local)
 * — o clique no ícone sobe até `ControleProcessosPage.jsx`, que refaz o
 * fetch da grid filtrado por tipo de ação (mesmo comportamento de
 * `handleOpenResumoProcessoByType`/`AntecedentesProcessosController` reais:
 * só os antecedentes elegíveis para Unificar/Juntar aparecem selecionáveis).
 * `tipoAcaoAtiva` (variável local, não state) é só a projeção
 * "`null` quando `SIMPLES`" desse prop, para minimizar o diff no resto do
 * componente.
 */
export default function ResumoAntecedenteAtual({
  idProcesso, dadosProcesso, idFaseProcesso, podeModificarFase, clientePermiteUnificar, fasesIds,
  idsAntecedentesSelecionados, onSucesso, tipoConsultaAntecedentes, onAlternarTipo,
}) {
  const tipoAcaoAtiva = tipoConsultaAntecedentes === tiposConsultasAntecedentesTypes.simples
    ? null
    : tipoConsultaAntecedentes;

  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // "Latest ref" — mesmo padrão de `onClickSimularQuitacao`
  // (`AbaAcompanhamento.jsx`): se o usuário trocar de processo antes da
  // resposta da mutação chegar, o resultado do processo antigo não deve
  // afetar o card/estado do processo novo.
  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;

  // Guarda síncrona contra duplo-clique — `enviando` (useState) só reflete
  // no próximo render; 2 cliques disparados antes desse render ainda
  // enxergam o closure antigo (`enviando === false`) e podem disparar a
  // mutação 2x. Mesmo padrão já documentado em `src/prototipo/CLAUDE.md`
  // (`DialogEnviarEmailDevedor.jsx`).
  const enviandoRef = useRef(false);

  // `confirmacaoAberta`/`enviando`/`erro` resetados ao trocar de processo —
  // mesmo padrão já documentado em `src/prototipo/CLAUDE.md` para
  // `acaoAtiva`/`BarraAcoesProcesso.jsx`. `tipoAcaoAtiva` NÃO é resetado
  // aqui (não é mais state local deste componente) — quem reseta
  // `tipoConsultaAntecedentes` ao trocar de processo é `ControleProcessosPage.jsx`.
  useEffect(() => {
    setConfirmacaoAberta(false);
    setEnviando(false);
    setErro('');
    // Achado em code review (Etapa 4): sem isso, `enviandoRef` continuava
    // travado por uma mutação em voo do processo ANTERIOR até o `finally`
    // dela rodar — uma nova tentativa de Confirmar no processo atual era
    // descartada em silêncio (`if (enviandoRef.current) return;`), sem
    // nenhum feedback visível, mesmo com `enviando`/`confirmacaoAberta` já
    // resetados e a UI parecendo livre para uso.
    enviandoRef.current = false;
  }, [idProcesso]);

  // `idFaseProcesso` vem de `sinalizacaoInfo.idFase` (mesma fonte que a tela
  // real usa para este gate, `additionalInfos.idFase`), NÃO de
  // `dadosProcesso.idFase` — achado em code review: `dadosProcesso`
  // (`buscarAcompanhamentoDoProcessoService`) não expõe esse campo com esse
  // nome; usar a fonte errada faria `cannotEnabled` nunca desabilitar nada
  // (fail-open) por comparar contra `NaN`.
  //
  // Fail-closed enquanto `fasesIds`/`idFaseProcesso` ainda não carregaram
  // (achado na 2ª rodada de review — mesma classe de bug do item acima,
  // repetida aqui): sem os 2 valores, não dá para confirmar que o processo
  // NÃO está em Acordo Seguro/Baixa, então `cannotEnabled` deve começar
  // `true` (desabilitado) até a resposta chegar, nunca `false` por padrão.
  const dadosDeFaseCarregados = Boolean(fasesIds) && idFaseProcesso != null;
  const idFaseProcessoNum = Number(idFaseProcesso);
  const cannotEnabled = !dadosDeFaseCarregados
    || Number(fasesIds?.idFaseAcordoSeguro) === idFaseProcessoNum
    || Number(fasesIds?.idFaseBaixa) === idFaseProcessoNum;

  const desabilitarUnificar = !hasUserPermissions(PERMISSAO_UNIFICAR)
    || !clientePermiteUnificar || !podeModificarFase || cannotEnabled;
  const desabilitarJuntar = !hasUserPermissions(PERMISSAO_JUNTAR) || !podeModificarFase || cannotEnabled;

  // Mesmo shape que a tela real monta em `screenEnabledStatus.idsSelected`
  // (`Map` de ids inicializado com o processo principal já `true`) — o
  // `idProcesso` principal viaja junto com os antecedentes marcados, tanto
  // no payload do service quanto na mensagem de confirmação.
  const idsOperacao = [idProcesso, ...idsAntecedentesSelecionados];

  const onClickConfirmar = () => {
    setConfirmacaoAberta(true);
  };

  const onConfirmarMutacao = async () => {
    if (enviandoRef.current) {
      return;
    }
    enviandoRef.current = true;
    const idProcessoAlvo = idProcesso;
    const tipo = tipoAcaoAtiva;
    setEnviando(true);
    try {
      if (tipo === tiposConsultasAntecedentesTypes.juntar) {
        await juntarProcessosService(idProcessoAlvo, idsOperacao);
      } else if (tipo === tiposConsultasAntecedentesTypes.unificar) {
        await unificarProcessosService(idProcessoAlvo, idsOperacao);
      }
      if (idProcessoRef.current === idProcessoAlvo) {
        // `tipoConsultaAntecedentes` (prop) é resetado para `SIMPLES` pelo
        // container dentro do próprio `onSucesso` (`onAntecedenteMutado`,
        // `ControleProcessosPage.jsx`) — não é mais responsabilidade deste
        // componente fechar o card. Por isso `onSucesso` é obrigatório
        // (`isRequired`, sem default): omiti-lo deixaria o card preso aberto
        // após uma mutação bem-sucedida, sem nenhum sinal visível (achado em
        // code review).
        onSucesso();
      }
    } catch (e) {
      if (idProcessoRef.current === idProcessoAlvo) {
        setErro(e.response?.data?.message || MENSAGEM_ERRO_PADRAO);
      }
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
      setConfirmacaoAberta(false);
    }
  };

  const getCliente = () => {
    if (dadosProcesso?.codigoCliente && dadosProcesso?.nomeCliente) {
      return `${dadosProcesso.codigoCliente} - ${dadosProcesso.nomeCliente}`;
    }
    return '';
  };

  const getDevedor = () => {
    if (dadosProcesso?.codigoDevedor && dadosProcesso?.nomeDevedor) {
      return `${dadosProcesso.codigoDevedor} - ${dadosProcesso.nomeDevedor}`;
    }
    return '';
  };

  const titulo = tipoAcaoAtiva === tiposConsultasAntecedentesTypes.unificar
    ? { texto: 'Unificar Processo', hint: 'A ação da Unificação será inserida no Histórico do Processo', rotulo: 'Unificar' }
    : { texto: 'Juntar Processo', hint: 'A ação de Juntar será inserida no Histórico do Processo', rotulo: 'Juntar' };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Unificar Processos">
          <span>
            <IconButton
              onClick={() => onAlternarTipo(tiposConsultasAntecedentesTypes.unificar)}
              disabled={desabilitarUnificar}
              size="small"
              sx={{
                color: tipoAcaoAtiva === tiposConsultasAntecedentesTypes.unificar ? C.navy : C.warmGrey,
                border: tipoAcaoAtiva === tiposConsultasAntecedentesTypes.unificar ? `2px solid ${C.navy}` : '1px solid transparent',
              }}
            >
              <FaObjectGroup size={18} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Juntar Processos">
          <span>
            <IconButton
              onClick={() => onAlternarTipo(tiposConsultasAntecedentesTypes.juntar)}
              disabled={desabilitarJuntar}
              size="small"
              sx={{
                color: tipoAcaoAtiva === tiposConsultasAntecedentesTypes.juntar ? C.navy : C.warmGrey,
                border: tipoAcaoAtiva === tiposConsultasAntecedentesTypes.juntar ? `2px solid ${C.navy}` : '1px solid transparent',
              }}
            >
              <FaObjectUngroup size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {tipoAcaoAtiva && (
        <Box sx={{
          mt: 1.5, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1,
        }}
        >
          <Typography sx={{ fontWeight: 700 }}>{titulo.texto}</Typography>
          <Typography variant="caption" sx={{ color: C.warmGrey, display: 'block', mb: 1 }}>
            {titulo.hint}
          </Typography>
          <Typography variant="body2">
            <b>Processo: </b>
            {idProcesso}
          </Typography>
          <Typography variant="body2">
            <b>Negociador: </b>
            {dadosProcesso?.nomeUsuario || ''}
          </Typography>
          <Typography variant="body2">
            <b>Cliente: </b>
            {getCliente()}
          </Typography>
          <Typography variant="body2">
            <b>Devedor: </b>
            {getDevedor()}
          </Typography>
          <Typography variant="body2">
            <b>Data da Abertura: </b>
            {dadosProcesso?.dataAbertura ? toBrazilianDate(dadosProcesso.dataAbertura) : ''}
          </Typography>
          <Typography variant="body2">
            <b>Data da Expiração: </b>
            {dadosProcesso?.dataExpiracao ? toBrazilianDate(dadosProcesso.dataExpiracao) : ''}
          </Typography>
          <Button
            variant="contained"
            color="success"
            size="small"
            disabled={idsAntecedentesSelecionados.length < 1 || enviando}
            onClick={onClickConfirmar}
            sx={{ mt: 1.5 }}
          >
            {titulo.rotulo}
          </Button>
        </Box>
      )}

      <Dialog open={confirmacaoAberta} onClose={() => setConfirmacaoAberta(false)}>
        <DialogTitle>{titulo.texto}</DialogTitle>
        <DialogContent>
          <DialogContentText>{getMensagemConfirmacao(tipoAcaoAtiva, idsOperacao)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmacaoAberta(false)} disabled={enviando}>Cancelar</Button>
          <Button onClick={onConfirmarMutacao} disabled={enviando} variant="contained" color="success">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Box>
  );
}

ResumoAntecedenteAtual.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dadosProcesso: PropTypes.object,
  idFaseProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  podeModificarFase: PropTypes.bool,
  clientePermiteUnificar: PropTypes.bool,
  fasesIds: PropTypes.shape({
    idFaseAcordoSeguro: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idFaseBaixa: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idFaseAcordoSeguroExterno: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idsAntecedentesSelecionados: PropTypes.array,
  onSucesso: PropTypes.func.isRequired,
  tipoConsultaAntecedentes: PropTypes.string,
  onAlternarTipo: PropTypes.func.isRequired,
};

ResumoAntecedenteAtual.defaultProps = {
  idProcesso: null,
  dadosProcesso: null,
  idFaseProcesso: null,
  podeModificarFase: false,
  clientePermiteUnificar: false,
  fasesIds: null,
  idsAntecedentesSelecionados: [],
  tipoConsultaAntecedentes: tiposConsultasAntecedentesTypes.simples,
};
